import { Component } from '@angular/core';
import { FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient, HttpErrorResponse, HttpStatusCode } from "@angular/common/http";
import { ConstantsService } from "../../constants.service";
import { MatSnackBar, MatSnackBarRef, TextOnlySnackBar } from "@angular/material/snack-bar";
import { getAuthErrorMessage } from '../auth-error-message.util';
import { AuthService } from '../auth.service';

@Component({
  selector: 'app-forgot-password',
  templateUrl: './forgot-password.component.html',
  styleUrls: ['./forgot-password.component.scss']
})
export class ForgotPasswordComponent {
  private apiUrl: string = '';
  isLoading = false;
  token: string = '';
  email: string = '';
  isEntityCreationFlow = false;
  showNewPassword = false;
  showConfirmPassword = false;
  passwordPattern: string = '';
  passwordDescriptionRule: string = '';
  forgotPasswordForm: FormGroup = new FormGroup({});
  resetPasswordForm: FormGroup = new FormGroup({});
  private passwordRulesSnackBarRef: MatSnackBarRef<TextOnlySnackBar> | null = null;

  constructor(
    private router: Router,
    private httpClient: HttpClient,
    private constantsService: ConstantsService,
    private activatedRoute: ActivatedRoute,
    private snackBar: MatSnackBar,
    private authService: AuthService,
  ) {
    this.initForgotPasswordForm();

    this.constantsService.onReady(() => {
      this.apiUrl = this.constantsService.getConstant('apiUrl');
      this.passwordPattern = this.constantsService.getConstant('passwordPattern');
      this.passwordDescriptionRule = this.constantsService.getConstant('passwordDescriptionRule');

      this.initResetPasswordForm();
      this.applyEmailPatternValidator();
    });

    this.activatedRoute.queryParams.subscribe((params) => {
      this.token = params['token'] || '';
      this.email = params['email'] || '';
      this.isEntityCreationFlow = this.token !== '' && this.isEntityCreationFlowEnabled(params['entity_creation_flow']);
      this.syncResetPasswordForm();
    });
  }

  initResetPasswordForm(): void {
    this.resetPasswordForm = new FormGroup({
      token: new FormControl(this.token, Validators.required),
      newPassword: new FormControl('', [Validators.required, Validators.pattern(this.passwordPattern)]),
      confirmPassword: new FormControl('', [Validators.required])
    }, {
      validators: this.passwordMatchValidator as ValidatorFn,
      updateOn: 'blur'
    });

    this.resetPasswordForm.get('newPassword')?.valueChanges.subscribe(() => {
      if (this.resetPasswordForm.get('newPassword')?.hasError('pattern')) {
        this.showPasswordRules();
        return;
      }

      this.dismissPasswordRules();
    });
  }

  initForgotPasswordForm(): void {
    this.forgotPasswordForm = new FormGroup({
      email: new FormControl('', [Validators.required])
    }, { updateOn: 'blur' });
  }

  private applyEmailPatternValidator(): void {
    const emailControl = this.forgotPasswordForm.get('email');
    emailControl?.addValidators(Validators.pattern(new RegExp(this.constantsService.getConstant('emailPattern'))));
    emailControl?.updateValueAndValidity();
  }

  hasError(form: FormGroup, errorName: string, controlName?: string): boolean {
    if (!form) {
      return false;
    }

    if (controlName === undefined) {
      return form.touched && form.hasError(errorName);
    }

    const control = form.controls[controlName];
    return control.touched && control.hasError(errorName);
  }

  passwordMatchValidator(form: FormGroup): { [key: string]: boolean } | null {
    return form.get('newPassword')?.value === form.get('confirmPassword')?.value ? null : { 'passwordMismatch': true };
  }

  togglePasswordVisibility(field: 'newPassword' | 'confirmPassword'): void {
    if (field === 'newPassword') {
      this.showNewPassword = !this.showNewPassword;
      return;
    }

    this.showConfirmPassword = !this.showConfirmPassword;
  }

  changePassword(): void {
    if (this.resetPasswordForm.invalid) {
      if (this.resetPasswordForm.get('newPassword')?.hasError('pattern')) {
        this.showPasswordRules();
      }

      return;
    }

    this.setLoadingState();
    const newPassword = this.resetPasswordForm.value.newPassword as string;

    this.httpClient.post(this.apiUrl + '/user/api/v2/auth/reset-password', { token: this.token, newPassword }, {
      withCredentials: true
    }).subscribe({
      next: () => {
        if (this.shouldAutoLoginAfterPasswordChange()) {
          this.authenticateCreatedUser(newPassword);
          return;
        }

        this.unsetLoadingState();
        this.snackBar.open('Password changed successfully', 'Close', {
          duration: 3000,
          panelClass: ['success-snackbar']
        });
        this.router.navigate(['/login']);
      },
      error: (error) => {
        console.error('Error changing password:', error);
        this.unsetLoadingState();
        this.snackBar.open(getAuthErrorMessage(error, 'Error changing password'), 'Close', {
          duration: 3000,
          panelClass: ['error-snackbar']
        });
      },
    })
  }

  showPasswordRules() {
    if (this.passwordRulesSnackBarRef) {
      return;
    }

    this.passwordRulesSnackBarRef = this.snackBar.open(this.passwordDescriptionRule, 'Close', {
      panelClass: ['error-snackbar', 'multiline-snackbar']
    });

    this.passwordRulesSnackBarRef.afterDismissed().subscribe(() => {
      this.passwordRulesSnackBarRef = null;
    });
  }

  requestPasswordResetLink(): void {
    if (this.forgotPasswordForm.invalid) {
      return;
    }

    this.setLoadingState();

    this.httpClient.post(this.apiUrl + '/user/api/v2/auth/forgot-password', this.forgotPasswordForm.value, {
      withCredentials: true
    }).subscribe({
      next: (response) => {
        this.unsetLoadingState();
        this.router.navigate(['/password-reset-sent']);
      },
      error: (error) => {
        this.snackBar.open(getAuthErrorMessage(error, 'Error sending password reset link'), 'Close', {
          duration: 3000,
          panelClass: ['error-snackbar']
        });
        this.unsetLoadingState();
      },
    });
  }

  private unsetLoadingState() {
    this.isLoading = false;
  }

  private setLoadingState() {
    this.isLoading = true;
  }

  private syncResetPasswordForm(): void {
    if (!this.resetPasswordForm.contains('token')) {
      return;
    }

    this.resetPasswordForm.patchValue({ token: this.token }, { emitEvent: false });
  }

  private isEntityCreationFlowEnabled(value: unknown): boolean {
    return value !== undefined && value !== null && value !== '' && value !== '0' && value !== 'false' && value !== false;
  }

  private shouldAutoLoginAfterPasswordChange(): boolean {
    return this.email !== '';
  }

  private authenticateCreatedUser(password: string): void {
    this.authService.login(this.email, password).then(() => {
      this.unsetLoadingState();
      this.router.navigate(['/shell']);
    }).catch((error: HttpErrorResponse) => {
      if (error?.error?.mfaToken && error.status === HttpStatusCode.PreconditionRequired) {
        this.unsetLoadingState();
        this.router.navigate(['/mfa'], { queryParams: { token: error.error.mfaToken, email: this.email } });
        return;
      }

      this.unsetLoadingState();
      this.snackBar.open(
        getAuthErrorMessage(error, 'Password changed successfully, but automatic sign in failed. Please sign in manually.'),
        'Close',
        {
          duration: 3000,
          panelClass: ['error-snackbar']
        }
      );
      this.router.navigate(['/login']);
    });
  }

  private dismissPasswordRules(): void {
    this.passwordRulesSnackBarRef?.dismiss();
  }
}
